import * as Sentry from '@sentry/react'
import { DotLottieReact, type DotLottie, type LoadErrorEvent } from '@lottiefiles/dotlottie-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SyncProgress } from '@/assets/icons'

const reportedAssetFailures = new Set<string>()

type LottieLoaderProps = {
  src: string
  size: number
  className?: string
  fallbackClassName: string
  loop: boolean
  autoplay: boolean
  ariaLabel: string
}

export function LottieLoader({ src, size, className, fallbackClassName, loop, autoplay, ariaLabel }: LottieLoaderProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const playerRef = useRef<DotLottie | null>(null)
  const srcRef = useRef(src)
  srcRef.current = src

  const handleLoadError = useCallback((event: LoadErrorEvent) => {
    const failedAsset = srcRef.current
    setFailedSrc(failedAsset)
    reportAssetFailure(failedAsset, event.error)
  }, [])

  const setPlayer = useCallback(
    (player: DotLottie | null) => {
      playerRef.current?.removeEventListener('loadError', handleLoadError)
      playerRef.current = player
      player?.addEventListener('loadError', handleLoadError)
    },
    [handleLoadError]
  )

  useEffect(
    () => () => {
      playerRef.current?.removeEventListener('loadError', handleLoadError)
    },
    [handleLoadError]
  )

  return (
    <div role="img" aria-label={ariaLabel}>
      {/* DotLottieReact initializes WebAssembly on mount, so check support before rendering the player. */}
      {typeof WebAssembly === 'undefined' || failedSrc === src ? (
        <span className={className} data-lottie-loader-fallback style={{ display: 'block', width: size, height: size }}>
          <SyncProgress className={`h-full w-full animate-spin motion-reduce:animate-none ${fallbackClassName}`} aria-hidden />
        </span>
      ) : (
        <DotLottieReact
          src={src}
          loop={loop}
          autoplay={autoplay}
          style={{ width: size, height: size }}
          className={className}
          dotLottieRefCallback={setPlayer}
        />
      )}
    </div>
  )
}

function reportAssetFailure(assetPath: string, error: Error) {
  if (reportedAssetFailures.has(assetPath)) return
  reportedAssetFailures.add(assetPath)

  Sentry.captureMessage('Agent V2 Lottie loader asset failed', {
    level: 'error',
    fingerprint: ['agent-v2-lottie-loader', assetPath],
    tags: {
      feature: 'agent-v2',
      assetPath,
      appVersion: APP_VERSION,
      serviceWorkerControlled: String(Boolean(navigator.serviceWorker?.controller)),
    },
    extra: {
      error: error.message,
      online: navigator.onLine,
      cookieEnabled: navigator.cookieEnabled,
      displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser',
      serviceWorkerControllerState: navigator.serviceWorker?.controller?.state ?? null,
      visibilityState: document.visibilityState,
      path: window.location.pathname,
    },
  })
}
